import { useQueryHelper } from '@anytag/shared-hooks';
import { checkIsReelPost } from '@anytag/shared-utils';
import { PostDetailStatistics, PostDetailsType } from '@anytag/shared-types';
import { SocialPostType, useInfluencerPostDetailQuery } from '@anytag/graphql';
import { PostModal, usePostModalContext } from '../shared';

export type TemplatePostDetailsType = Pick<PostDetailsType, 'id' | 'influencerId'>;

export interface TemplateProps {
  className?: string;
  hasSimilarPosts?: boolean;
}

export const Template = ({ className, hasSimilarPosts = false }: TemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { closeModal, selectedPost } = usePostModalContext<TemplatePostDetailsType>();
  const { data, loading } = useInfluencerPostDetailQuery({
    skip: !selectedPost?.id || !selectedPost?.influencerId,
    variables: {
      postId: Number(selectedPost?.id),
      pk: Number(selectedPost?.influencerId),
      needSimilarPosts: hasSimilarPosts
    },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      closeModal?.();
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  });

  const details = data?.influencerPostDetail;
  const statistics: PostDetailStatistics = (() => {
    switch (details?.socialMedia) {
      case SocialPostType.INSTAGRAM:
        return { likes: details?.likes, reach: undefined, comments: details?.comments };
      case SocialPostType.INSTAGRAM_REEL:
      case SocialPostType.YOUTUBE:
        return { likes: details?.likes, views: details?.views, comments: details?.comments };
      case SocialPostType.TWITTER:
        return { likes: details?.likes, retweets: details?.shares, replies: details?.comments };
      case SocialPostType.THREADS:
        return { likes: details?.likes, replies: details?.comments };
      case SocialPostType.DOUYIN:
        return { likes: details?.likes, views: details?.views, shares: details.shares, comments: details?.comments };
      case SocialPostType.TIKTOK:
        return {
          likes: details?.likes,
          views: details?.views,
          comments: details?.comments,
          shares: details?.shares
        };
      case SocialPostType.FACEBOOK:
      case SocialPostType.FACEBOOK_PAGE:
        return {
          reactions: details?.reactions,
          comments: details?.comments,
          shares: details?.shares,
          engagement: details?.engagement
        };
      case SocialPostType.XHS:
        return {
          likes: details?.likes,
          views: details?.views,
          saves: details?.saves
        };
      default:
        return {};
    }
  })();

  return (
    <PostModal.DetailsTemplate
      loading={loading}
      className={className}
      data={
        details
          ? {
              statistics,
              caption: details?.caption,
              postUrl: details?.postUrl,
              postDate: details?.postDate,
              similarPosts: details.similarPosts || [],
              comments: details?.commentsContent || [],
              isReel: checkIsReelPost(details?.postUrl),
              socialMedia: details?.socialMedia as SocialPostType,
              images: !details?.thumbNail ? [] : [details.thumbNail],
              info: {
                avatar: details?.avatar,
                name: details?.influencerName,
                followers: details?.followersCount,
                id: String(details?.influencerId ?? '')
              }
            }
          : undefined
      }
    />
  );
};
